import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { formatDate, formatValue } from '@/shared/lib/format';
import type { Citation, Panel } from '../api/schemas';
import { PanelFrame } from './PanelFrame';

type Props = { panel: Extract<Panel, { kind: 'technical' }>; citation: Citation | undefined };

function tone(level: string) {
  if (level === 'Spot') return 'text.primary';
  return level.startsWith('S') ? 'success.main' : 'error.main';
}

/** Support below, spot in the middle, resistance above: the levels laid out the way a trader reads them. */
export function TechnicalPanel({ panel, citation }: Props) {
  return (
    <PanelFrame
      title={`${panel.instrument} technical levels`}
      meta={`as of ${formatDate(panel.asOf)}`}
      citation={citation}
    >
      <Stack
        component="ol"
        aria-label="Support, spot and resistance"
        direction="row"
        sx={{ listStyle: 'none', m: 0, px: 1.75, pb: 1.5, gap: 0.75, overflowX: 'auto' }}
      >
        {panel.levels.map((l) => (
          <Stack
            component="li"
            key={l.level}
            sx={{
              alignItems: 'center',
              minWidth: 64,
              py: 0.75,
              px: 1,
              borderRadius: 1,
              bgcolor: l.level === 'Spot' ? 'surface.sunken' : 'transparent',
              border: 1,
              borderColor: l.level === 'Spot' ? 'text.disabled' : 'divider',
            }}
          >
            <Typography variant="caption" sx={{ color: tone(l.level), fontWeight: 600 }}>
              {l.level}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: l.level === 'Spot' ? 600 : 400 }}
            >
              {formatValue(l.value)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </PanelFrame>
  );
}
